import React from 'react';
import { Link } from 'react-router-dom';

interface CustomButtonProps {
    text: React.ReactNode;
    to?: string;
    theme?: "primary" | "outlined" | "light";
    size?: "sm" | "mid" | "lg";
    className?: string;
    onClick?: () => void;
    disabled?: boolean;
    type?: "button" | "submit" | "reset";
}

const BASE =
    "group relative inline-flex w-fit min-w-max items-center justify-center gap-2.5 overflow-hidden rounded-full font-normal tracking-brand whitespace-nowrap transition-all duration-300 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-400 active:scale-[.97]";

const THEMES = {
    primary:
        "bg-gradient-to-r from-primary-400 to-dark-primary-500 text-white shadow-glow hover:-translate-y-0.5 hover:shadow-[0_16px_36px_-10px_rgba(66,154,237,.7)]",
    outlined:
        "border border-primary-500 bg-transparent text-primary-500 hover:bg-primary-500 hover:text-white",
    light:
        "bg-white text-primary-900 shadow-[0_10px_30px_-10px_rgba(0,0,0,.35)] hover:-translate-y-0.5 hover:bg-primary-50",
};

const SIZES = {
    sm: "h-10 px-5 text-sm",
    mid: "h-12 px-8 text-[0.9375rem] sm:px-9 sm:text-base",
    lg: "h-14 px-10 text-lg",
};

const CustomButton: React.FC<CustomButtonProps> = ({
    text,
    to,
    theme = "primary",
    size = "mid",
    className = "",
    onClick,
    disabled = false,
    type = "button",
}) => {
    const combinedClass = [
        BASE,
        THEMES[theme],
        SIZES[size],
        disabled ? "pointer-events-none cursor-not-allowed opacity-60" : "cursor-pointer",
        className,
    ].join(" ");

    const isExternal = to && /^(https?:)?\/\//i.test(to);

    if (to && !disabled) {
        if (isExternal) {
            return (
                <a href={to} target="_blank" rel="noopener noreferrer" className={combinedClass} onClick={onClick}>
                    {text}
                </a>
            );
        }

        return (
            <Link to={to} className={combinedClass} onClick={onClick}>
                {text}
            </Link>
        );
    }

    return (
        <button type={type} className={combinedClass} onClick={onClick} disabled={disabled}>
            {text}
        </button>
    );
};

export default CustomButton;
